import { Button } from '@bambi/component-library/src/components/Button';
import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import type { MouseEventHandler } from 'react';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';

import type { NotificationMetaDataCallback } from './types';
import {
  type CredentialRead,
  useCredentialsRetrieveQuery,
} from 'src/common/external/bambi-api/bambiApi';
import { type NotificationRead } from 'src/common/external/bambi-api/notificationsApi';
import { formatFullName } from 'src/common/util/formatFullName';

export function CredentialExpiringSoonRenderer({
  notification,
}: {
  notification: NotificationRead;
}) {
  const { data: credential, isLoading } = useCredentialsRetrieveQuery(
    {
      id: notification.ref_entity_id ?? '',
    },
    {
      skip: !notification.ref_entity_id,
    }
  );

  if (isLoading) {
    return <LoadingIndicator variant="small" />;
  }

  if (!credential) {
    return <div className="text-sm">Credential not found</div>;
  }

  if (credential.credential_type.category === 'Team') {
    return <MembershipCredentialType credential={credential} />;
  } else if (credential.credential_type.category === 'Vehicle') {
    return <VehicleCredentialType credential={credential} />;
  }

  return <MiscCredentialType credential={credential} />;
}

function MembershipCredentialType({
  credential,
}: {
  credential: CredentialRead;
}) {
  const navigate = useNavigate();

  const handleOpenCredentialsPage = useCallback<MouseEventHandler>(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      navigate('/team/member-credentials');
    },
    [navigate]
  );

  return (
    <div className="flex flex-col text-sm">
      <div>
        <strong>Who: </strong>
        {credential.membership ? formatFullName(credential.membership) : null}
      </div>
      <div>
        <strong>Type: </strong>
        {credential.credential_type.name}
      </div>
      <div>
        <strong>Expires At: </strong>
        {credential.expiration_date}
      </div>
      <Button
        variant="anchor-inline"
        justifyContent="start"
        onClick={handleOpenCredentialsPage}
      >
        View on Team &gt; Credentials
      </Button>
    </div>
  );
}

function VehicleCredentialType({ credential }: { credential: CredentialRead }) {
  const navigate = useNavigate();

  const handleOpenCredentialsPage = useCallback<MouseEventHandler>(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      navigate('/fleet/credentials');
    },
    [navigate]
  );

  return (
    <div className="flex flex-col text-sm">
      <div>
        <strong>Vehicle: </strong>
        {credential.vehicle ? (credential.vehicle.nickname ?? 'No Name') : null}
      </div>
      <div>
        <strong>Type: </strong>
        {credential.credential_type.name}
      </div>
      <div>
        <strong>Expires At: </strong>
        {credential.expiration_date}
      </div>
      <Button
        variant="anchor-inline"
        justifyContent="start"
        onClick={handleOpenCredentialsPage}
      >
        View on Vehicle &gt; Credentials
      </Button>
    </div>
  );
}

function MiscCredentialType({ credential }: { credential: CredentialRead }) {
  return (
    <div className="flex flex-col text-sm">
      <div>
        <strong>Type: </strong>
        {credential.credential_type.name}
      </div>
      <div>
        <strong>Expires At: </strong>
        {credential.expiration_date}
      </div>
    </div>
  );
}

export const getMetaData: NotificationMetaDataCallback = (_notification) => {
  return {
    title: 'Credential Expiring Soon',
  };
};
